import { useState, useEffect, useRef } from 'react';
import { useChatStore } from '../../stores/chat';
import type { Thread, Task } from '../../lib/api';
import { formatDistanceToNowStrict } from 'date-fns';
import TasksSidebar from '../tasks/TasksSidebar';
import InternalSidebar from './InternalSidebar';
import SegmentedControl from '../ui/SegmentedControl';
import { Avatar } from '../ui/Avatar';
import { Lock, Check } from '../../lib/icons';

type ClientFilter = 'all' | 'unread';
function isUnread(t: Thread): boolean { return parseInt(t.unread_count ?? '0') > 0; }

type SubTab = 'clients' | 'internal' | 'tasks';

interface Props {
  subTab: SubTab;
  onSubTabChange: (t: SubTab) => void;
  onSelectThread: (id: string) => void;
  onSelectTask: (task: Task) => void;
  onStartDm: (userId: string) => Promise<void>;
  onCreateGroup: (name: string, participantIds: string[]) => Promise<void>;
  style?: React.CSSProperties;
}

const SECTION_META: Record<SubTab, { label: string; desc: string; badge?: string }> = {
  clients:  { label: 'Clients',  desc: 'Client threads' },
  internal: { label: 'Internal', desc: 'Team threads & members' },
  tasks:    { label: 'Tasks',    desc: 'Open & completed tasks' },
};

function relativeTime(ts: string | null | undefined): string {
  if (!ts) return '';
  try { return formatDistanceToNowStrict(new Date(ts), { addSuffix: false }); }
  catch { return ''; }
}

export default function Sidebar({ subTab, onSubTabChange, onSelectThread, onSelectTask, onStartDm, onCreateGroup, style }: Props) {
  const threads = useChatStore((s) => s.threads);
  const activeThreadId = useChatStore((s) => s.activeThreadId);
  const clientThreads = threads.filter((t) => t.type === 'client');
  const [clientFilter, setClientFilter] = useState<ClientFilter>('all');
  const unreadClientCount = clientThreads.filter(isUnread).length;
  const visibleClientThreads = clientFilter === 'unread' ? clientThreads.filter(isUnread) : clientThreads;

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const selectorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!dropdownOpen) return;
    function handleOutside(e: MouseEvent) {
      if (selectorRef.current && !selectorRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [dropdownOpen]);

  function selectSection(t: SubTab) {
    onSubTabChange(t);
    setDropdownOpen(false);
  }

  const meta = SECTION_META[subTab];

  return (
    <div style={{
      background: 'var(--vs1)',
      borderRight: '1px solid var(--shell-border)',
      display: 'flex', flexDirection: 'column',
      padding: '10px 8px', gap: 8,
      width: 280, flexShrink: 0,
      ...style,
    }}>
      {/* ── Upper floating selector card ── */}
      <div
        ref={selectorRef}
        style={{
          background: 'var(--surface-raised)',
          border: '1px solid var(--shell-border)',
          borderRadius: 8,
          flexShrink: 0,
          position: 'relative',
          zIndex: 20,
        }}
      >
        {/* Trigger row */}
        <div
          onClick={() => setDropdownOpen((o) => !o)}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '10px 12px', cursor: 'pointer', userSelect: 'none',
          }}
        >
          <span style={{ fontSize: '0.88rem', fontWeight: 700, color: 'var(--shell-text)' }}>
            {meta.label}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{
              fontSize: '0.68rem', fontWeight: 700,
              padding: '2px 7px', borderRadius: 3,
              background: 'var(--accent-tint)', color: 'var(--v2)',
            }}>
              {subTab === 'tasks' ? 'Tasks' : subTab === 'internal' ? 'Team' : 'Clients'}
            </span>
            <span style={{
              color: 'var(--shell-xs)', fontSize: '0.7rem', lineHeight: 1,
              display: 'inline-block',
              transform: dropdownOpen ? 'rotate(180deg)' : 'none',
              transition: 'transform .18s',
            }}>▾</span>
          </div>
        </div>

        {/* Dropdown */}
        {dropdownOpen && (
          <div style={{
            position: 'absolute', top: 'calc(100% + 5px)', left: 0, right: 0,
            background: 'var(--surface-raised)',
            border: '1px solid var(--shell-border)',
            borderRadius: 8,
            overflow: 'hidden',
            boxShadow: '0 12px 32px rgba(0,0,0,.55)',
            zIndex: 30,
          }}>
            {(['clients', 'internal', 'tasks'] as const).map((t) => (
              <div
                key={t}
                onClick={() => selectSection(t)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 12px', cursor: 'pointer',
                  borderBottom: t !== 'tasks' ? '1px solid var(--shell-border)' : undefined,
                  background: subTab === t ? 'var(--accent-tint)' : 'transparent',
                  transition: 'background .12s',
                }}
                onMouseEnter={(e) => {
                  if (subTab !== t) (e.currentTarget as HTMLDivElement).style.background = 'var(--hover)';
                }}
                onMouseLeave={(e) => {
                  if (subTab !== t) (e.currentTarget as HTMLDivElement).style.background = 'transparent';
                }}
              >
                <div>
                  <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--shell-text)' }}>
                    {SECTION_META[t].label}
                  </div>
                  <div style={{ fontSize: '0.67rem', color: 'var(--shell-xs)', marginTop: 1 }}>
                    {SECTION_META[t].desc}
                  </div>
                </div>
                {subTab === t && <Check size={15} strokeWidth={2} style={{ color: 'var(--v2)' }} />}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Lower floating list card ── */}
      <div style={{
        background: 'var(--surface-raised)',
        border: '1px solid var(--shell-border)',
        borderRadius: 8,
        flex: 1,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {subTab === 'tasks' && (
          <TasksSidebar onSelectTask={onSelectTask} />
        )}

        {subTab === 'internal' && (
          <InternalSidebar
            onSelectThread={onSelectThread}
            onStartDm={onStartDm}
            onCreateGroup={onCreateGroup}
          />
        )}

        {subTab === 'clients' && (
          <>
            <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--shell-border)' }}>
              <SegmentedControl
                size="sm"
                fullWidth
                value={clientFilter}
                onChange={setClientFilter}
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'unread', label: unreadClientCount > 0 ? `Unread (${unreadClientCount})` : 'Unread' },
                ]}
              />
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {visibleClientThreads.length === 0 && (
                <div style={{ padding: '24px 16px', color: 'var(--shell-xs)', fontSize: '0.78rem', textAlign: 'center' }}>
                  {clientFilter === 'unread' ? 'No unread threads' : 'No client threads yet'}
                </div>
              )}
              {visibleClientThreads.map((thread) => (
                <ClientThreadRow
                  key={thread.id}
                  thread={thread}
                  active={activeThreadId === thread.id}
                  onClick={() => onSelectThread(thread.id)}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ClientThreadRow({ thread, active, onClick }: {
  thread: Thread; active: boolean; onClick: () => void;
}) {
  const unread = parseInt(thread.unread_count ?? '0') > 0;
  const c = thread.contact;
  const displayName = c?.display_name || c?.name || thread.name || thread.client_account_name || 'Thread';
  const company = c?.company ?? thread.client_account_name ?? null;

  return (
    <div
      onClick={onClick}
      style={{
        padding: '9px 14px', cursor: 'pointer',
        borderBottom: '1px solid var(--row-divider)',
        display: 'flex', gap: 9, alignItems: 'flex-start',
        borderLeft: active ? '2px solid var(--active-border)' : '2px solid transparent',
        background: active ? 'var(--active-bg)' : 'transparent',
        transition: 'background .12s',
      }}
    >
      <Avatar name={displayName} size={36} tone="client" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--shell-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {displayName}
          </span>
          {company && (
            <span style={{
              flexShrink: 0, fontSize: '0.62rem', fontWeight: 600, padding: '1px 6px', borderRadius: 3,
              background: 'rgba(29,158,117,.15)', color: 'var(--v2)',
              maxWidth: 110, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {company}
            </span>
          )}
        </div>
        <div style={{
          fontSize: '0.74rem', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          color: thread.last_message_is_internal ? 'rgba(239,159,39,.8)' : 'var(--shell-xs)',
          display: 'flex', alignItems: 'center', gap: 4,
        }}>
          {thread.last_message_is_internal && <Lock size={10} strokeWidth={1.8} style={{ flexShrink: 0 }} />}
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {thread.last_message_sender && `${thread.last_message_sender}: `}
            {thread.last_message ?? 'No messages yet'}
          </span>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
        <span style={{ fontSize: '0.68rem', color: 'var(--shell-xs)' }}>
          {relativeTime(thread.last_message_at)}
        </span>
        <div style={{ width: 7, height: 7, borderRadius: 2, background: unread ? 'var(--v1)' : 'transparent' }} />
      </div>
    </div>
  );
}
